// Imports => React
import React, {useMemo, useRef, useEffect, useState, memo} from 'react';
import {Props} from './ac-image.proptypes';
import clsx from 'clsx';
import Blazy from 'blazy';

// Imports => Constants
import {TYPES} from '@asdf-constants';

const _CLASSES = {
  MAIN: 'ac-image',
  WRP: 'ac-image-wrp',
  TRANSPARENT: 'ac-image-wrp--transparent',
  LOADED: 'ac-image-wrp--loaded',
  BLAZY: 'b-lazy',
  IMAGE: 'ac-image--image',
  BACKGROUND: 'ac-image--background-image',
  ERROR: 'ac-image__error',
};

const AcImage = memo(
  ({
    id = `ac-image-id`,
    placeholder = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==',
    source,
    type = 'background',
    alt = '',
    callback,
    transparent = true,
    wrpClassName,
    className,
    ...rest
  }) => {
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);

    const $wrp = useRef(null);
    const $element = useRef(null);

    let timer = null;
    let delay = 50;
    let blazy = null;

    useEffect(() => {
      init();
      return () => clearTimeout(timer);
    }, [source]);

    const init = () => {
      if (timer) clearTimeout(timer);
      if (!loaded && !error && source) {
        timer = setTimeout(() => {
          if (blazy === null) {
            blazy = new Blazy(getBlazyConfig);
          } else {
            blazy.revalidate();
          }
          delay = 10;
        }, delay);
      }
    };

    const getBlazyConfig = useMemo(() => {
      return {
        selector: `#${id}`,
        container: '#ac-scroller',
        offset: 250,
        loadInvisible: true,
        success: () => {
          if ($element !== null && $element.current !== null) {
            setLoaded(true);
            if (callback) callback(true);
          }
        },
        error: () => {
          if ($element !== null && $element.current !== null) {
            const err = 'The image source is either missing, broken or unsupported';
            setError(err);
            if (callback) callback(false);
          }
        },
      };
    }, [id, callback, source]);

    const getErrorClassNames = useMemo(() => {
      return clsx(_CLASSES.ERROR);
    }, []);

    const getMainClassNames = useMemo(() => {
      return clsx([_CLASSES.BLAZY, _CLASSES.MAIN, className]);
    }, []);

    const getBackgroundStyleClassNames = useMemo(() => {
      return clsx([_CLASSES.BLAZY, _CLASSES.MAIN, _CLASSES.BACKGROUND, className]);
    });

    const getWrapperClassNames = useMemo(() => {
      return clsx([
        _CLASSES.WRP,
        wrpClassName,
        loaded && _CLASSES.LOADED,
        transparent && _CLASSES.TRANSPARENT,
        type === TYPES.IMAGE && _CLASSES.IMAGE,
      ]);
    }, [loaded, transparent]);

    const renderError = useMemo(() => {
      if (!error) return null;

      return (
        <div
          className={getErrorClassNames}
          dangerouslySetInnerHTML={{
            __html: error,
          }}
        />
      );
    }, [error]);

    const renderInlineImage = useMemo(() => {
      return (
        <img
          src={placeholder}
          data-src={source}
          className={getMainClassNames}
          alt={alt}
          ref={$element}
          id={id}
          loading={'lazy'}
          {...rest}
        />
      );
    }, [source, alt, placeholder, id]);

    const renderBackgroundImage = useMemo(() => {
      return (
        <div
          data-src={source}
          className={getBackgroundStyleClassNames}
          ref={$element}
          id={id}
          {...rest}
        />
      );
    }, [source, id]);

    const renderImage = useMemo(() => {
      if (type === TYPES.BACKGROUND) return renderBackgroundImage;
      else if (type === TYPES.IMAGE) return renderInlineImage;
    }, [type, renderInlineImage, renderBackgroundImage]);

    return (
      <div className={getWrapperClassNames} ref={$wrp}>
        {renderImage}
        {renderError}
      </div>
    );
  }
);

AcImage.displayName = 'AcImage';
AcImage.propTypes = Props;

export {AcImage};
